<template>
  <div
    class="conversations-list-wrap"
    :class="{
      hide: !showConversationList,
      'list--full-width': isOnExpandedLayout,
    }"
  >
    <div
      v-if="canShowAlertFilter"
      class="row alert-filter"
      style="margin: 7px 14px"
    >
      <div
        class="columns text-center"
        :class="isOnExpandedLayout ? 'medium-1' : 'medium-2'"
      >
        <fluent-icon icon="warning" size="40" />
      </div>

      <div class="medium-9 columns">
        <h3>{{ $t('CHAT_LIST.ALERT.WARNING') }}</h3>
        <p v-html="$t('CHAT_LIST.ALERT.TEXT', { filter: pageTitle })"></p>
      </div>
    </div>

    <div
      class="chat-list__top"
      :class="{ filter__applied: hasAppliedFiltersOrActiveFolders }"
    >
      <div class="chat-list__header-left">
        <div class="filter-header-row">
          <div class="filter-left">
            <woot-sidemenu-icon />
            <woot-button
              v-tooltip.right="$t('FILTER.TOOLTIP_LABEL')"
              variant="smooth"
              color-scheme="secondary"
              icon="filter"
              size="tiny"
              class="hide-on-mobile"
              @click="onToggleAdvanceFiltersModal"
            />
            <conversation-basic-filter
              v-if="!hasAppliedFiltersOrActiveFolders"
              @change-filter="onChangeFilter"
              @export-conversations="onExportConversations"
            />
          </div>
        </div>

        <div
          v-if="!hasAppliedFiltersOrActiveFolders"
          class="status-filter-wrapper"
        >
          <div class="status-buttons">
            <button
              v-for="status in chatStatusItems"
              :key="status.id"
              :class="[
                'status-button',
                { 'status-button--active': chatStatus === status.id },
              ]"
              @click="onChangeFilter('status', status.id)"
            >
              {{ $t(`CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${status.id}.TEXT`) }}
            </button>
          </div>
        </div>
      </div>

      <div class="filter--actions">
        <div v-if="hasAppliedFilters && !hasActiveFolders">
          <woot-button
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.ADD.SAVE_BUTTON')"
            size="tiny"
            variant="smooth"
            color-scheme="secondary"
            icon="save"
            @click="onClickOpenAddFoldersModal"
          />
          <woot-button
            v-tooltip.top-end="$t('FILTER.CLEAR_BUTTON_LABEL')"
            size="tiny"
            variant="smooth"
            color-scheme="alert"
            icon="dismiss-circle"
            @click="resetAndFetchData"
          />
        </div>

        <div v-if="hasActiveFolders">
          <woot-button
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.EDIT.EDIT_BUTTON')"
            size="tiny"
            variant="smooth"
            color-scheme="secondary"
            icon="edit"
            @click="onToggleAdvanceFiltersModal"
          />
          <woot-button
            v-tooltip.top-end="$t('FILTER.CUSTOM_VIEWS.DELETE.DELETE_BUTTON')"
            size="tiny"
            variant="smooth"
            color-scheme="alert"
            icon="delete"
            @click="onClickOpenDeleteFoldersModal"
          />
        </div>
        <div class="layout-switch">
          <switch-layout
            v-show="true"
            :is-on-expanded-layout="isOnExpandedLayout"
            @toggle="$emit('toggle-layout')"
          />
        </div>
      </div>
    </div>

    <keep-alive>
      <add-custom-views
        v-if="showAddFoldersModal"
        :custom-views-query="foldersQuery"
        :open-last-saved-item="openLastSavedItemInFolder"
        @close="onCloseAddFoldersModal"
      />
    </keep-alive>

    <keep-alive>
      <delete-custom-views
        v-if="showDeleteFoldersModal"
        :show-delete-popup.sync="showDeleteFoldersModal"
        :active-custom-view="activeFolder"
        :custom-views-id="foldersId"
        :open-last-item-after-delete="openLastItemAfterDeleteInFolder"
        @close="onCloseDeleteFoldersModal"
      />
    </keep-alive>

    <keep-alive>
      <chat-type-tabs
        :items="assigneeTabItems"
        :active-tab="activeAssigneeTab"
        class="tab--chat-type"
        @chatTabChange="updateAssigneeTab"
      />
    </keep-alive>
    <p
      v-if="!chatListLoading && !conversationList.length"
      class="content-box no-conversations-message"
    >
      {{ $t('CHAT_LIST.LIST.404') }}
    </p>
    <conversation-bulk-actions
      v-if="selectedConversations.length"
      :conversations="selectedConversations"
      :all-conversations-selected="allConversationsSelected"
      :selected-inboxes="uniqueInboxes"
      :show-open-action="allSelectedConversationsStatus('open')"
      :show-resolved-action="allSelectedConversationsStatus('resolved')"
      :show-snoozed-action="allSelectedConversationsStatus('snoozed')"
      :closing-reason-blocked-count="closingReasonBlockedCount"
      :open-opportunity-blocked-count="openOpportunityBlockedCount"
      @select-all-conversations="selectAllConversations"
      @assign-agent="onAssignAgent"
      @update-conversations="onUpdateConversations"
      @assign-labels="onAssignLabels"
      @assign-team="onAssignTeamsForBulk"
    />

    <div
      ref="activeConversation"
      class="conversations-list"
      :class="{ 'is-context-menu-open': isContextMenuOpen }"
    >
      <conversation-card
        v-for="chat in conversationList"
        :key="chat.id"
        :active-label="label"
        :active-product-label="productLabel"
        :active-topic-label="topicLabel"
        :active-concurrent-label="concurrentLabel"
        :team-id="teamId"
        :folders-id="foldersId"
        :chat="chat"
        :conversation-type="conversationType"
        :show-assignee="showAssigneeInConversationCard"
        :selected="isConversationSelected(chat.id)"
        @select-conversation="selectConversation"
        @de-select-conversation="deSelectConversation"
        @assign-agent="onAssignAgent"
        @assign-team="onAssignTeam"
        @assign-label="onAssignLabels"
        @update-conversation-status="toggleConversationStatus"
        @context-menu-toggle="onContextMenuToggle"
        @mark-as-unread="markAsUnread"
        @assign-priority="assignPriority"
      />

      <div v-if="chatListLoading" class="text-center">
        <span class="spinner" />
      </div>

      <woot-button
        v-if="!hasCurrentPageEndReached && !chatListLoading"
        variant="clear"
        size="expanded"
        @click="loadMoreConversations"
      >
        {{ $t('CHAT_LIST.LOAD_MORE_CONVERSATIONS') }}
      </woot-button>

      <p
        v-if="showEndOfListMessage"
        class="text-center text-muted end-of-list-text"
      >
        {{ $t('CHAT_LIST.EOF') }}
      </p>
    </div>

    <woot-modal
      :show.sync="showAdvancedFilters"
      :on-close="closeAdvanceFiltersModal"
      size="medium"
    >
      <conversation-advanced-filter
        v-if="showAdvancedFilters"
        :initial-filter-types="advancedFilterTypes"
        :initial-applied-filters="appliedFilter"
        :active-folder-name="activeFolderName"
        :on-close="closeAdvanceFiltersModal"
        :is-folder-view="hasActiveFolders"
        @applyFilter="onApplyFilter"
        @updateFolder="onUpdateSavedFilter"
      />
    </woot-modal>
    <woot-modal
      :show.sync="showExportFiltersModal"
      :on-close="closeExportFiltersModal"
      size="medium"
    >
      <export-conversations-filter
        v-if="showExportFiltersModal"
        :on-close="closeExportFiltersModal"
        @export="onSubmitExportFilters"
      />
    </woot-modal>

    <woot-modal
      :show.sync="showClosingReasonPopup"
      :on-close="hideClosingReasonPopup"
    >
      <add-closing-reason
        :inbox="closingReasonInbox"
        :current-chat="closingReasonChat"
        :options="closingReasonOptions"
        @on-close="hideClosingReasonPopup"
      />
    </woot-modal>

    <woot-modal
      :show.sync="showResolveBlockedModal"
      :on-close="closeResolveBlockedModal"
    >
      <opportunity-resolve-blocked-modal
        v-if="resolveBlockedOpportunity"
        :opportunity="resolveBlockedOpportunity"
        @close="closeResolveBlockedModal"
      />
    </woot-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';

import ConversationAdvancedFilter from './widgets/conversation/ConversationAdvancedFilter';
import ExportConversationsFilter from './widgets/conversation/ExportConversationsFilter';
import ConversationBasicFilter from './widgets/conversation/ConversationBasicFilter';
import ChatTypeTabs from './widgets/ChatTypeTabs';
import ConversationCard from './widgets/conversation/ConversationCard';
import timeMixin from '../mixins/time';
import eventListenerMixins from 'shared/mixins/eventListenerMixins';
import conversationMixin from '../mixins/conversations';
import wootConstants from 'dashboard/constants/globals';
import advancedFilterTypes from './widgets/conversation/advancedFilterItems';
import filterQueryGenerator from '../helper/filterQueryGenerator.js';
import AddCustomViews from 'dashboard/routes/dashboard/customviews/AddCustomViews';
import DeleteCustomViews from 'dashboard/routes/dashboard/customviews/DeleteCustomViews.vue';
import ConversationBulkActions from './widgets/conversation/conversationBulkActions/Index.vue';
import AddClosingReason from 'dashboard/routes/dashboard/settings/closingreasons/Modal';
import OpportunityResolveBlockedModal from 'dashboard/components/widgets/conversation/OpportunityResolveBlockedModal.vue';
import alertMixin from 'shared/mixins/alertMixin';
import filterMixin from 'shared/mixins/filterMixin';
import agentMixin from 'dashboard/mixins/agentMixin';
import {
  isConversationStatusChangeBlocked,
  splitConversationsByClosingReasonRequirement,
} from 'dashboard/helper/conversationClosingReasonHelper';
import { fetchOpenOpportunityForConversation } from 'dashboard/helper/opportunityResolveGuard';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import languages from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import countries from 'shared/constants/countries';
import { generateValuesForEditCustomViews } from 'dashboard/helper/customViewsHelper';
import Cookies from 'js-cookie';
import { setCookieWithDomain } from './../../sdk/cookieHelpers';
import SwitchLayout from 'dashboard/routes/dashboard/conversation/search/SwitchLayout';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';

import {
  hasPressedAltAndJKey,
  hasPressedAltAndKKey,
} from 'shared/helpers/KeyboardHelpers';
import { conversationListPageURL } from '../helper/URLHelper';
import {
  isOnMentionsView,
  isOnUnattendedView,
  isOnUnreadView,
  isLastMessageClientView,
} from '../store/modules/conversations/helpers/actionHelpers';
import { CONVERSATION_EVENTS } from '../helper/AnalyticsHelper/events';

export default {
  components: {
    AddCustomViews,
    ChatTypeTabs,
    ConversationCard,
    ConversationAdvancedFilter,
    ExportConversationsFilter,
    DeleteCustomViews,
    ConversationBulkActions,
    ConversationBasicFilter,
    SwitchLayout,
    AddClosingReason,
    OpportunityResolveBlockedModal,
  },
  mixins: [
    agentMixin,
    timeMixin,
    conversationMixin,
    eventListenerMixins,
    alertMixin,
    filterMixin,
  ],
  props: {
    conversationInbox: {
      type: [String, Number],
      default: 0,
    },
    teamId: {
      type: [String, Number],
      default: 0,
    },
    label: {
      type: String,
      default: '',
    },
    productLabel: {
      type: String,
      default: '',
    },
    topicLabel: {
      type: String,
      default: '',
    },
    concurrentLabel: {
      type: String,
      default: '',
    },
    conversationType: {
      type: String,
      default: '',
    },
    foldersId: {
      type: [String, Number],
      default: 0,
    },
    showConversationList: {
      default: true,
      type: Boolean,
    },
    isOnExpandedLayout: {
      default: false,
      type: Boolean,
    },
  },
  data() {
    return {
      activeAssigneeTab: wootConstants.ASSIGNEE_TYPE.ME,
      activeStatus: wootConstants.STATUS_TYPE.OPEN,
      activeSortBy: wootConstants.SORT_BY_TYPE.LATEST,
      showAdvancedFilters: false,
      advancedFilterTypes: advancedFilterTypes.map(filter => ({
        ...filter,
        attributeName: this.$t(`FILTER.ATTRIBUTES.${filter.attributeI18nKey}`),
      })),
      chatStatusItems: [
        {
          id: 'snoozed',
          TEXT: this.$t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.snoozed.TEXT'),
        },
        {
          id: 'open',
          TEXT: this.$t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.open.TEXT'),
        },
        {
          id: 'resolved',
          TEXT: this.$t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.resolved.TEXT'),
        },
      ],

      // chatsOnView is to store the chats that are currently visible on the screen,
      // which mirrors the conversationList.
      chatsOnView: [],
      foldersQuery: {},
      showAddFoldersModal: false,
      showDeleteFoldersModal: false,
      selectedConversations: [],
      selectedInboxes: [],
      showClosingReasonPopup: false,
      closingReasonConversationId: null,
      closingReasonOptions: {},
      closingReasonBlockedCount: 0,
      openOpportunityBlockedCount: 0,
      showResolveBlockedModal: false,
      resolveBlockedOpportunity: null,
      isContextMenuOpen: false,
      appliedFilter: [],
      timeoutId: null,
      watchers: [],
      showGlowBorder: false,
      timeIntervalConversations: null,
      autoRefreshTimerId: null,
      resetConversationsEnabled: !!LocalStorage.get(
        LOCAL_STORAGE_KEYS.RESET_CONVERSATIONS
      ),
      showExportFiltersModal: false,
      isExportingAll: false,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      currentUser: 'getCurrentUser',
      chatLists: 'getAllConversations',
      mineChatsList: 'getMineChats',
      getFilteredChatsByCustomFolder: 'getFilteredChatsByCustomFolder',
      customViewChats: 'getCustomViewChats',
      allChatList: 'getAllStatusChats',
      unAssignedChatsList: 'getUnAssignedChats',
      chatListLoading: 'getChatListLoadingStatus',
      activeInbox: 'getSelectedInbox',
      conversationStats: 'conversationStats/getStats',
      appliedFilters: 'getAppliedConversationFilters',
      folders: 'customViews/getCustomViews',
      inboxes: 'inboxes/getInboxes',
      teamsList: 'teams/getTeams',
      inboxesList: 'inboxes/getInboxes',
      campaigns: 'campaigns/getAllCampaigns',
      labels: 'labels/getLabels',
      chatStatusFilter: 'getChatStatusFilter',
      campaignFilter: 'getCampaignFilter',
    }),
    canShowAlertFilter() {
      const routes = this.routesWithFilters;
      const show = routes.includes(this.$route.name);

      this.timeoutId = setTimeout(() => this.setCss(show), 300);

      return show;
    },
    routesWithFilters() {
      return [
        'conversation_mentions',
        'conversation_through_mentions',
        'conversation_unattended',
        'conversation_through_unattended',
        'conversation_unread',
        'conversation_through_unread',
        'conversation_last_message_client',
        'conversation_through_last_message_client',
        'team_conversations',
        'inbox_dashboard',
        'label_conversations',
        'product_label_conversations',
        'conversations_through_product_label',
        'topic_label_conversations',
        'conversations_through_topic_label',
        'concurrent_label_conversations',
        'conversations_through_concurrent_label',
      ];
    },
    hasAppliedFilters() {
      return this.appliedFilters.length !== 0;
    },
    hasActiveFolders() {
      return this.activeFolder && this.foldersId !== 0;
    },
    hasAppliedFiltersOrActiveFolders() {
      return this.hasAppliedFilters || this.hasActiveFolders;
    },
    showEndOfListMessage() {
      return (
        this.conversationList.length &&
        this.hasCurrentPageEndReached &&
        !this.chatListLoading
      );
    },
    currentUserDetails() {
      const { id, name } = this.currentUser;
      return {
        id,
        name,
      };
    },
    assigneeTabItems() {
      const ASSIGNEE_TYPE_TAB_KEYS = {
        me: {
          label_count: 'mineCount',
          roles: [
            'administrator',
            'agent',
            'seller',
            'supervisor',
            'team_supervisor',
            'analyzer',
            'assistant',
            'embed',
          ],
          icon: 'chat',
        },
        unassigned: {
          label_count: 'unAssignedCount',
          roles: [
            'administrator',
            'agent',
            'seller',
            'supervisor',
            'team_supervisor',
            'analyzer',
            'assistant',
            'embed',
          ],
          icon: 'queue',
        },
        all: {
          label_count: 'allCount',
          roles: [
            'administrator',
            'agent',
            'supervisor',
            'team_supervisor',
            'analyzer',
            'assistant',
            'embed',
          ],
          icon: 'people-team',
        },
      };

      if (this.agentRules?.unassigned === false) {
        delete ASSIGNEE_TYPE_TAB_KEYS.unassigned;
      }

      return Object.keys(ASSIGNEE_TYPE_TAB_KEYS)
        .map(key => {
          const assigneeTypeTab = ASSIGNEE_TYPE_TAB_KEYS[key];
          const count =
            this.conversationStats[assigneeTypeTab.label_count] || 0;
          return {
            key,
            name: this.$t(`CHAT_LIST.ASSIGNEE_TYPE_TABS.${key}`),
            count,
            roles: assigneeTypeTab.roles,
            icon: assigneeTypeTab.icon,
          };
        })
        .filter(tab => tab.roles.includes(this.currentRole));
    },
    showAssigneeInConversationCard() {
      return (
        this.hasAppliedFiltersOrActiveFolders ||
        this.activeAssigneeTab === wootConstants.ASSIGNEE_TYPE.ALL
      );
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.activeInbox);
    },
    currentPage() {
      return this.$store.getters['conversationPage/getCurrentPageFilter'](
        this.activeAssigneeTab
      );
    },
    currentPageFilterKey() {
      return this.hasAppliedFiltersOrActiveFolders
        ? 'appliedFilters'
        : this.activeAssigneeTab;
    },
    currentFiltersPage() {
      return this.$store.getters['conversationPage/getCurrentPageFilter'](
        this.currentPageFilterKey
      );
    },
    hasCurrentPageEndReached() {
      return this.$store.getters['conversationPage/getHasEndReached'](
        this.currentPageFilterKey
      );
    },
    activeAssigneeTabCount() {
      const { activeAssigneeTab } = this;
      const count = this.assigneeTabItems.find(
        item => item.key === activeAssigneeTab
      ).count;
      return count;
    },
    conversationFilters() {
      return {
        inboxId: this.conversationInbox ? this.conversationInbox : undefined,
        assigneeType: this.activeAssigneeTab,
        status: this.activeStatus,
        sortBy: this.activeSortBy,
        page: this.conversationListPagination,
        campaignFilter:
          this.campaignFilter && this.campaignFilter !== 'all'
            ? this.campaignFilter
            : undefined,
        labels: this.label ? [this.label] : undefined,
        productLabels: this.productLabel ? [this.productLabel] : undefined,
        topicLabels: this.topicLabel ? [this.topicLabel] : undefined,
        concurrentLabels: this.concurrentLabel
          ? [this.concurrentLabel]
          : undefined,
        teamId: this.teamId || undefined,
        folders: undefined,
        conversationType: this.conversationType || undefined       
      };
    },
    conversationListPagination() {
      return this.currentPage + 1;
    },
    pageTitle() {
      if (this.hasAppliedFilters) {
        return this.$t('CHAT_LIST.TAB_HEADING');
      }
      if (this.inbox.name) {
        return this.inbox.name;
      }
      if (this.activeTeam.name) {
        return this.activeTeam.name;
      }
      if (this.label) {
        return `#${this.label}`;
      }
      if (this.productLabel) {
        return `${this.$t('CONVERSATION.CARD.PRODUCTS')}: ${this.productLabel}`;
      }
      if (this.topicLabel) {
        return `${this.$t('CONVERSATION.CARD.TOPICS')}: ${this.topicLabel}`;
      }
      if (this.concurrentLabel) {
        return `${this.$t('CONVERSATION.CARD.CONCURRENTS')}: ${
          this.concurrentLabel
        }`;
      }
      if (this.conversationType === 'mention') {
        return this.$t('CHAT_LIST.MENTION_HEADING');
      }
      if (this.conversationType === 'participating') {
        return this.$t('CONVERSATION_PARTICIPANTS.SIDEBAR_MENU_TITLE');
      }
      if (this.conversationType === 'unattended') {
        return this.$t('CHAT_LIST.UNATTENDED_HEADING');
      }
      if (this.conversationType === 'unread') {
        return this.$t('CHAT_LIST.UNREAD_HEADING');
      }
      if (this.conversationType === 'lasted') {
        return this.$t('CHAT_LIST.LASTED_HEADING');
      }
      if (this.hasActiveFolders) {
        return this.activeFolder.name;
      }
      return this.$t('CHAT_LIST.TAB_HEADING');
    },
    conversationList() {
      let baseList = [];

      if (this.hasAppliedFiltersOrActiveFolders) {
        const folder = this.activeFolder || {};
        const query = folder.query ? folder.query : {};
        const payload = Array.isArray(query.payload) ? query.payload : [];
        baseList = [...this.getFilteredChatsByCustomFolder(payload)];
      } else {
        const filters = this.conversationFilters;

        if (this.activeAssigneeTab === 'me') {
          baseList = [...this.mineChatsList(filters)];
        } else if (this.activeAssigneeTab === 'unassigned') {
          baseList = [...this.unAssignedChatsList(filters)];
        } else {
          baseList = [...this.allChatList(filters)];
        }
      }

      if (this.activeAssigneeTab === 'me') {
        baseList = baseList.filter(
          c => c.meta?.assignee?.id === this.currentUser.id
        );
      } else if (this.activeAssigneeTab === 'unassigned') {
        baseList = baseList.filter(c => !c.meta?.assignee?.id);
      }

      return baseList;
    },
    activeFolder() {
      if (this.foldersId) {
        const activeView = this.folders.filter(
          view => view.id === Number(this.foldersId)
        );
        const [firstValue] = activeView;
        return firstValue;
      }
      return undefined;
    },
    activeFolderName() {
      return this.activeFolder?.name;
    },
    activeTeam() {
      if (this.teamId) {
        return this.$store.getters['teams/getTeam'](this.teamId);
      }
      return {};
    },
    allConversationsSelected() {
      return (
        this.conversationList.length === this.selectedConversations.length &&
        this.conversationList.every(el =>
          this.selectedConversations.includes(el.id)
        )
      );
    },
    uniqueInboxes() {
      return [...new Set(this.selectedInboxes)];
    },
    closingReasonChat() {
      return (
        this.$store.getters.getConversationById(
          this.closingReasonConversationId
        ) || {}
      );
    },
    closingReasonInbox() {
      return this.$store.getters['inboxes/getInbox'](
        this.closingReasonChat.inbox_id
      );
    },
    isOpportunityPipelinesEnabled() {
      return this.$store.getters['accounts/isFeatureEnabledonAccount'](
        this.$route.params.accountId,
        FEATURE_FLAGS.OPPORTUNITY_PIPELINES
      );
    },
    chatStatus() {
      return this.chatStatusFilter || wootConstants.STATUS_TYPE.OPEN;
    },
  },
  watch: {
    activeTeam() {
      this.resetAndFetchData();
    },
    conversationInbox() {
      this.resetAndFetchData();
    },
    label() {
      this.resetAndFetchData();
    },
    conversationType() {
      this.resetAndFetchData();
    },
    activeFolder() {
      this.resetAndFetchData();
    },
    chatLists() {
      this.chatsOnView = this.conversationList;
    },
    'conversationStats.priorityCount'(newCount) {
      if (newCount > 0) {
        this.restartGlowEffect();
      } else {
        this.showGlowBorder = false;
      }
    },
  },
  beforeMount() {
    this.setCookieFilters();
  },
  beforeDestroy() {
    bus.$off('fetch_conversation_stats');
    bus.$off('fetch_all_conversation');
    bus.$off('verify_conversation_team_private');
    bus.$off('conversations_force_reset_and_fetch_data');

    if (this.$refs.activeConversation) {
      const allConversations = this.$refs.activeConversation.querySelectorAll(
        'div.conversations-list div.conversation'
      );
      allConversations.forEach(conversation => {
        conversation.removeEventListener('click', this.handleKeyEvents);
      });
    }

    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }

    if (this.watchers.length) {
      this.watchers.forEach(unwatch => unwatch());
    }

    if (this.timeIntervalConversations) {
      clearInterval(this.timeIntervalConversations);
    }

    this.stopAutoRefreshTimer();

    window.removeEventListener('keydown', this.handleKeyEvents);
  },
  async mounted() {
    if(parseInt(this.foldersId) > 0 && this.folders.length == 0){
      await this.$store.dispatch('customViews/get', 'conversation');     
    }
        
    this.$store.dispatch('setChatStatusFilter', this.activeStatus);
    this.$store.dispatch('setChatSortFilter', this.activeSortBy);
    this.resetAndFetchData();

    if (this.hasActiveFolders) {
      this.$store.dispatch('campaigns/get');
    }

    bus.$on('fetch_conversation_stats', () => {
      this.$store.dispatch('conversationStats/get', this.conversationFilters);
    });

    bus.$on('fetch_all_conversation', params => {
      console.warn(`[CHATLIST] ⚠️  Evento fetch_all_conversation recebido`, {
        params: params?.id || params,
        currentRoute: this.$route.name,
        hasParams: !!params,
      });
      console.trace('[CHATLIST] stack trace do evento');

      if (this.routesWithFilters.includes(this.$route.name)) {
        console.log(
          `[CHATLIST] Rota com filtros, fazendo fetchAllConversations`
        );
        const filters = JSON.parse(JSON.stringify(this.conversationFilters));
        filters.page = 1;
        this.$store.dispatch('fetchAllConversations', filters);
      }
      if (params) {
        console.log(`[CHATLIST] Tem params, executando forceResetAndFetchData`);
        this.forceResetAndFetchData();
      }
    });

    bus.$on('verify_conversation_team_private', conversation => {
      console.log(
        `[CHATLIST] Evento verify_conversation_team_private recebido para conversa #${conversation.id}`,
        {
          team: conversation.meta?.team?.name,
          teamId: conversation.meta?.team?.id,
          teamPrivate: conversation.meta?.team?.private,
          currentUserId: this.currentUser.id,
        }
      );

      const {
        meta: { team },
      } = conversation;

      if (team.private && !team.members.includes(this.currentUser.id)) {
        console.warn(
          `[CHATLIST] ⚠️  REMOVENDO conversa #${conversation.id} - usuário não é membro do time privado`
        );
        this.$store.dispatch('removeConversation', conversation);
      } else {
        console.log(
          `[CHATLIST] Usuário é membro do time ou time não é privado, mantendo conversa`
        );
      }
    });

    this.watchersList();

    if (this.conversationStats.priorityCount > 0) {
      this.restartGlowEffect();
    }

    bus.$on('conversations_force_reset_and_fetch_data', () => {
      this.forceResetAndFetchData();
    });

    if (this.resetConversationsEnabled) {
      this.timeIntervalConversations = setInterval(() => {
        this.forceResetAndFetchData();
      }, 1 * 60 * 1000);
    }

    this.startAutoRefreshTimer();
  },
  methods: {
    async onChangeFilter(type, value) {
      if (type === 'status') {
        this.activeStatus = value;
        await this.$store.dispatch('setChatStatusFilter', value);
      }

      if (type === 'sort') {
        this.activeSortBy = value;
        await this.$store.dispatch('setChatSortFilter', value);
      }

      if (type === 'campaign') {
        await this.$store.dispatch('setCampaignFilter', value);
      }

      this.$emit('change-filter', type, value);

      this.resetAndFetchData();
    },
    async onExportConversations() {
      if (!this.selectedConversations.length) {
        this.showExportFiltersModal = true;
        return;
      }

      try {
        await this.$store.dispatch('conversationsExport/export', {
          conversation_ids: this.selectedConversations,
          email: this.currentUser.email,
        });

        this.showAlert(this.$t('CHAT_LIST.EXPORT.STARTED'));
        this.selectedConversations = [];
      } catch (error) {
        this.showAlert(this.$t('CHAT_LIST.EXPORT.ERROR'));
      }
    },
    async exportConversations(conversationIds) {
      try {
        await this.$store.dispatch('conversationsExport/export', {
          conversation_ids: conversationIds,
          email: this.currentUser.email,
        });

        this.showAlert(this.$t('CHAT_LIST.EXPORT.STARTED'));
        this.selectedConversations = [];
      } catch (error) {
        this.showAlert(this.$t('CHAT_LIST.EXPORT.ERROR'));
      }
    },
    closeExportFiltersModal() {
      this.showExportFiltersModal = false;
    },
    async onSubmitExportFilters(appliedFilters) {
      this.showExportFiltersModal = false;

      try {
        await this.$store.dispatch('conversationsExport/exportWithFilters', {
          email: this.currentUser.email,
          payload: filterQueryGenerator(appliedFilters).payload,
        });

        this.showAlert(this.$t('CHAT_LIST.EXPORT.STARTED'));
      } catch (error) {
        this.showAlert(this.$t('CHAT_LIST.EXPORT.ERROR'));
      }
    },
    setCss(show) {
      const content = 'ul.menu.vertical li:first-child a.secondary-menu--title';
      const html = document.querySelector(content);

      if (html) {
        html.style.fontWeight = show ? 'bold' : '500';
        html.style.color = show ? '#000' : '#22469b';
      }
    },
    onApplyFilter(payload) {
      this.resetBulkActions();
      this.foldersQuery = filterQueryGenerator(payload);
      this.$store.dispatch('conversationPage/reset');
      this.$store.dispatch('emptyAllConversations');
      this.fetchFilteredConversations(payload);
    },
    onUpdateSavedFilter(payload, folderName) {
      const payloadData = {
        ...this.activeFolder,
        name: folderName,
        query: filterQueryGenerator(payload),
      };
      this.$store.dispatch('customViews/update', payloadData);
      this.closeAdvanceFiltersModal();
    },
    onClickOpenAddFoldersModal() {
      this.showAddFoldersModal = true;
    },
    onCloseAddFoldersModal() {
      this.showAddFoldersModal = false;
    },
    onClickOpenDeleteFoldersModal() {
      this.showDeleteFoldersModal = true;
    },
    onCloseDeleteFoldersModal() {
      this.showDeleteFoldersModal = false;
    },
    onToggleAdvanceFiltersModal() {
      if (!this.hasAppliedFilters && !this.hasActiveFolders) {
        this.initializeExistingFilterToModal();
      } else if (!this.hasActiveFolders) {
        this.initializeContextFilters();
      }
      if (this.hasActiveFolders) {
        this.initializeFolderToFilterModal(this.activeFolder);
      }
      this.showAdvancedFilters = true;
    },
    closeAdvanceFiltersModal() {
      this.showAdvancedFilters = false;
      this.appliedFilter = [];
    },
    setParamsForEditFolderModal() {
      // Here we are setting the params for edit folder modal to show the existing values.

      // For agent, team, inboxes,and campaigns we get only the id's from the query.
      // So we are mapping the id's to the actual values.

      // For labels we get the name of the label from the query.
      // If we delete the label from the label list then we will not be able to show the label name.

      // For custom attributes we get only attribute key.
      // So we are mapping it to find the input type of the attribute to show in the edit folder modal.
      const params = {
        agents: this.agentList,
        teams: this.teamsList,
        inboxes: this.inboxesList,
        labels: this.labels,
        campaigns: this.campaigns,
        languages: languages,
        countries: countries,
        filterTypes: advancedFilterTypes,
        allCustomAttributes: this.$store.getters[
          'attributes/getAttributesByModel'
        ]('conversation_attribute'),
      };
      return params;
    },
    initializeFolderToFilterModal(activeFolder) {
      // Here we are setting the params for edit folder modal.
      //  To show the existing values. when we click on edit folder button.

      // Here we get the query from the active folder.
      // And we are mapping the query to the actual values.
      // To show in the edit folder modal by the help of generateValuesForEditCustomViews helper.
      const query = activeFolder?.query?.payload;
      if (!Array.isArray(query)) return;

      this.appliedFilter.push(
        ...query.map(filter => ({
          attribute_key: filter.attribute_key,
          attribute_model: filter.attribute_model,
          filter_operator: filter.filter_operator,
          values: Array.isArray(filter.values)
            ? generateValuesForEditCustomViews(
                filter,
                this.setParamsForEditFolderModal()
              )
            : [],
          query_operator: filter.query_operator,
          custom_attribute_type: filter.custom_attribute_type,
        }))
      );
    },
    getKeyboardListenerParams() {
      const allConversations = this.$refs.activeConversation.querySelectorAll(
        'div.conversations-list div.conversation'
      );
      const activeConversation = this.$refs.activeConversation.querySelector(
        'div.conversations-list div.conversation.active'
      );
      const activeConversationIndex = [...allConversations].indexOf(
        activeConversation
      );
      const lastConversationIndex = allConversations.length - 1;
      return {
        allConversations,
        activeConversation,
        activeConversationIndex,
        lastConversationIndex,
      };
    },
    handleKeyEvents(e) {
      if (hasPressedAltAndJKey(e)) {
        const { allConversations, activeConversationIndex } =
          this.getKeyboardListenerParams();
        if (activeConversationIndex === -1) {
          allConversations[0].click();
        }
        if (activeConversationIndex >= 1) {
          allConversations[activeConversationIndex - 1].click();
        }
      }
      if (hasPressedAltAndKKey(e)) {
        const {
          allConversations,
          activeConversationIndex,
          lastConversationIndex,
        } = this.getKeyboardListenerParams();
        if (activeConversationIndex === -1) {
          allConversations[lastConversationIndex].click();
        } else if (activeConversationIndex < lastConversationIndex) {
          allConversations[activeConversationIndex + 1].click();
        }
      }
    },
    resetAndFetchData() {
      this.resetBulkActions();
      this.$store.dispatch('conversationPage/reset');
      this.$store.dispatch('emptyAllConversations');
      this.appliedFilter = [];
      this.$store.dispatch('clearConversationFilters');
      if (this.hasActiveFolders) {
        const payload = this.activeFolder?.query || {};
        this.fetchSavedFilteredConversations(payload);
        return;
      }
      if (this.foldersId) {
        return;
      }
      this.fetchConversations();
    },
    fetchConversations(forceReset = false) {
      if (this.hasAppliedFiltersOrActiveFolders) {
        const payload = this.appliedFilters?.length
          ? this.appliedFilters
          : this.activeFolder?.query?.payload || [];

        this.fetchFilteredConversations(payload);
        return;
      }

      this.$store
        .dispatch('fetchAllConversations', {
          ...this.conversationFilters,
          reset: forceReset,
        })
        .then(() => {
          this.$emit('conversation-load');
        });
    },
    forceResetAndFetchData() {
      this.$store.dispatch('conversationPage/reset');
      if (this.hasAppliedFiltersOrActiveFolders) {
        const payload = this.appliedFilters?.length
          ? this.appliedFilters
          : this.activeFolder?.query?.payload || [];
        this.fetchFilteredConversations(payload);
        return;
      }
      this.fetchConversations(true);
    },
    startAutoRefreshTimer() {
      this.scheduleNextAutoRefresh();
      this.autoRefreshTimerId = setInterval(() => {
        this.runAutoRefreshTick();
      }, wootConstants.CONVERSATION_LIST_AUTO_REFRESH_INTERVAL_MS);
    },
    stopAutoRefreshTimer() {
      if (this.autoRefreshTimerId) {
        clearInterval(this.autoRefreshTimerId);
        this.autoRefreshTimerId = null;
      }
      this.$store.dispatch('setConversationListNextRefreshAt', null);
    },
    scheduleNextAutoRefresh() {
      this.$store.dispatch(
        'setConversationListNextRefreshAt',
        Date.now() + wootConstants.CONVERSATION_LIST_AUTO_REFRESH_INTERVAL_MS
      );
    },
    runAutoRefreshTick() {
      if (!this.chatListLoading) {
        this.forceResetAndFetchData();
      }
      this.scheduleNextAutoRefresh();
    },
    loadMoreConversations() {
      if (!this.hasAppliedFiltersOrActiveFolders) {
        this.fetchConversations();
      }
      if (this.hasActiveFolders) {
        const payload = this.activeFolder.query;
        this.fetchSavedFilteredConversations(payload);
      }
      if (this.hasAppliedFilters) {
        this.fetchFilteredConversations(this.appliedFilters);
      }
    },
    fetchFilteredConversations(payload) {
      let page = this.currentFiltersPage + 1;
      this.$store
        .dispatch('fetchFilteredConversations', {
          queryData: filterQueryGenerator(payload),
          page,
        })
        .then(() => {
          this.$emit('conversation-load');
        });
      this.showAdvancedFilters = false;
    },
    fetchSavedFilteredConversations(payload) {
      let page = this.currentFiltersPage + 1;
      this.$store
        .dispatch('fetchFilteredConversations', {
          queryData: payload,
          page,
        })
        .then(() => {
          this.$emit('conversation-load');
        });
    },
    updateAssigneeTab(selectedTab) {
      if (this.activeAssigneeTab !== selectedTab) {
        this.resetBulkActions();
        bus.$emit('clearSearchInput');
        this.activeAssigneeTab = selectedTab;
        if (!this.currentPage) {
          this.fetchConversations();
        }
      }
    },
    resetBulkActions() {
      this.selectedConversations = [];
      this.selectedInboxes = [];
      this.closingReasonBlockedCount = 0;
      this.openOpportunityBlockedCount = 0;
    },
    onBasicFilterChange(value, type) {
      if (type === 'status') {
        this.activeStatus = value;
      } else {
        this.activeSortBy = value;
      }
      this.setFiltersInCookie();
      this.resetAndFetchData();
    },
    openLastSavedItemInFolder() {
      const lastItemOfFolder = this.folders[this.folders.length - 1];
      const lastItemId = lastItemOfFolder.id;
      this.$router.push({
        name: 'folder_conversations',
        params: { id: lastItemId },
      });
    },
    openLastItemAfterDeleteInFolder() {
      if (this.folders.length > 0) {
        this.openLastSavedItemInFolder();
      } else {
        this.$router.push({ name: 'home' });
        this.fetchConversations();
      }
    },
    isConversationSelected(id) {
      return this.selectedConversations.includes(id);
    },
    selectConversation(conversationId, inboxId) {
      this.selectedConversations.push(conversationId);
      this.selectedInboxes.push(inboxId);
      this.closingReasonBlockedCount = 0;
      this.openOpportunityBlockedCount = 0;
    },
    deSelectConversation(conversationId, inboxId) {
      this.selectedConversations = this.selectedConversations.filter(
        item => item !== conversationId
      );
      this.selectedInboxes = this.selectedInboxes.filter(
        item => item !== inboxId
      );
      this.closingReasonBlockedCount = 0;
      this.openOpportunityBlockedCount = 0;
    },
    selectAllConversations(check) {
      if (check) {
        this.selectedConversations = this.conversationList.map(item => item.id);
        this.selectedInboxes = this.conversationList.map(item => item.inbox_id);
      } else {
        this.resetBulkActions();
      }
    },
    // Same method used in context menu, conversationId being passed from there.
    async onAssignAgent(agent, conversationId = null) {
      try {
        await this.$store.dispatch('bulkActions/process', {
          type: 'Conversation',
          ids: conversationId || this.selectedConversations,
          fields: {
            assignee_id: agent.id,
          },
        });
        this.selectedConversations = [];
        if (conversationId) {
          this.showAlert(
            this.$t(
              'CONVERSATION.CARD_CONTEXT_MENU.API.AGENT_ASSIGNMENT.SUCCESFUL',
              {
                agentName: agent.name,
                conversationId,
              }
            )
          );
        } else {
          this.showAlert(this.$t('BULK_ACTION.ASSIGN_SUCCESFUL'));
        }
      } catch (err) {
        this.showAlert(this.$t('BULK_ACTION.ASSIGN_FAILED'));
      }
    },
    async assignPriority(priority, conversationId = null) {
      this.$store.dispatch('setCurrentChatPriority', {
        priority,
        conversationId,
      });
      this.$store
        .dispatch('assignPriority', { conversationId, priority })
        .then(() => {
          this.$track(CONVERSATION_EVENTS.CHANGE_PRIORITY, {
            newValue: priority,
            from: 'Context menu',
          });
          this.showAlert(
            this.$t('CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL', {
              priority,
              conversationId,
            })
          );
        });
    },
    async markAsUnread(conversationId) {
      try {
        await this.$store.dispatch('markMessagesUnread', {
          id: conversationId,
        });
        const {
          params: { accountId, inbox_id: inboxId, label, teamId },
          name,
        } = this.$route;
        let conversationType = '';
        if (isOnMentionsView({ route: { name } })) {
          conversationType = 'mention';
        } else if (isOnUnattendedView({ route: { name } })) {
          conversationType = 'unattended';
        } else if (isOnUnreadView({ route: { name } })) {
          conversationType = 'unread';
        } else if (isLastMessageClientView({ route: { name } })) {
          conversationType = 'lasted';
        }
        await this.$router.push(
          conversationListPageURL({
            accountId,
            conversationType: conversationType,
            customViewId: this.foldersId,
            inboxId,
            label,
            teamId,
          })
        );
      } catch (error) {
        // Ignore error
      }
    },
    async onAssignTeam(team, conversationId = null) {
      try {
        await this.$store.dispatch('assignTeam', {
          conversationId,
          teamId: team.id,
        });
        this.showAlert(
          this.$t(
            'CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.SUCCESFUL',
            {
              team: team.name,
              conversationId,
            }
          )
        );
      } catch (error) {
        this.showAlert(
          this.$t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.FAILED')
        );
      }
    },
    // Same method used in context menu, conversationId being passed from there.
    async onAssignLabels(labels, conversationId = null) {
      try {
        await this.$store.dispatch('bulkActions/process', {
          type: 'Conversation',
          ids: conversationId || this.selectedConversations,
          labels: {
            add: labels,
          },
        });
        this.selectedConversations = [];
        if (conversationId) {
          this.showAlert(
            this.$t(
              'CONVERSATION.CARD_CONTEXT_MENU.API.LABEL_ASSIGNMENT.SUCCESFUL',
              {
                labelName: labels[0],
                conversationId,
              }
            )
          );
        } else {
          this.showAlert(this.$t('BULK_ACTION.LABELS.ASSIGN_SUCCESFUL'));
        }
      } catch (err) {
        this.showAlert(this.$t('BULK_ACTION.LABELS.ASSIGN_FAILED'));
      }
    },
    async onAssignTeamsForBulk(team) {
      try {
        await this.$store.dispatch('bulkActions/process', {
          type: 'Conversation',
          ids: this.selectedConversations,
          fields: {
            team_id: team.id,
          },
        });
        this.selectedConversations = [];
        this.showAlert(this.$t('BULK_ACTION.TEAMS.ASSIGN_SUCCESFUL'));
      } catch (err) {
        this.showAlert(this.$t('BULK_ACTION.TEAMS.ASSIGN_FAILED'));
      }
    },
    async onUpdateConversations(status) {
      const {
        allowedIds,
        blockedIds,
      } = splitConversationsByClosingReasonRequirement(
        this.selectedConversations,
        this.$store,
        status
      );

      this.closingReasonBlockedCount = blockedIds.length;

      if (!allowedIds.length) {
        return;
      }

      try {
        const response = await this.$store.dispatch('bulkActions/process', {
          type: 'Conversation',
          ids: allowedIds,
          fields: {
            status,
          },
        });
        const openOpportunityBlockedIds = response?.blocked_ids || [];
        this.openOpportunityBlockedCount = openOpportunityBlockedIds.length;

        this.selectedConversations = [...blockedIds, ...openOpportunityBlockedIds];
        this.selectedInboxes = this.selectedConversations.map(
          id => this.$store.getters.getConversationById(id)?.inbox_id
        );
        if (openOpportunityBlockedIds.length < allowedIds.length) {
          this.showAlert(this.$t('BULK_ACTION.UPDATE.UPDATE_SUCCESFUL'));
        }
      } catch (err) {
        this.showAlert(this.$t('BULK_ACTION.UPDATE.UPDATE_FAILED'));
      }
    },
    async toggleConversationStatus(conversationId, status, snoozedUntil) {
      if (
        status === wootConstants.STATUS_TYPE.RESOLVED &&
        this.isOpportunityPipelinesEnabled
      ) {
        const conversation = this.$store.getters.getConversationById(
          conversationId
        );
        const openOpportunity = await fetchOpenOpportunityForConversation(
          conversation?.primary_key
        );
        if (openOpportunity) {
          this.resolveBlockedOpportunity = openOpportunity;
          this.showResolveBlockedModal = true;
          return;
        }
      }

      if (
        isConversationStatusChangeBlocked({
          store: this.$store,
          conversationId,
          status,
        })
      ) {
        const options =
          status === wootConstants.STATUS_TYPE.SNOOZED
            ? { snooze: snoozedUntil }
            : {};
        this.openClosingReasonPopup(conversationId, options);
        return;
      }

      this.$store
        .dispatch('toggleStatus', {
          conversationId,
          status,
          snoozedUntil,
        })
        .then(() => {
          this.showAlert(this.$t('CONVERSATION.CHANGE_STATUS'));
        })
        .catch(error => {
          this.showAlert(
            parseAPIErrorResponse(error) ||
              this.$t('CONVERSATION.CHANGE_STATUS_FAILED')
          );
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    openClosingReasonPopup(conversationId, options = {}) {
      this.closingReasonConversationId = conversationId;
      this.closingReasonOptions = options;
      this.showClosingReasonPopup = true;
    },
    hideClosingReasonPopup() {
      this.showClosingReasonPopup = false;
      this.closingReasonConversationId = null;
      this.closingReasonOptions = {};
    },
    closeResolveBlockedModal() {
      this.showResolveBlockedModal = false;
      this.resolveBlockedOpportunity = null;
    },
    allSelectedConversationsStatus(status) {
      if (!this.selectedConversations.length) return false;
      return this.selectedConversations.every(item => {
        return this.$store.getters.getConversationById(item).status === status;
      });
    },
    onContextMenuToggle(state) {
      this.isContextMenuOpen = state;
    },
    getCookieFilters() {
      return Cookies.getJSON('cx_chat_filters_and_orders');
    },
    setFiltersInCookie() {
      const data = {
        basic: {
          activeSortBy: this.activeSortBy,
          activeStatus: this.activeStatus,
        },
      };

      setCookieWithDomain('cx_chat_filters_and_orders', data);
    },
    setCookieFilters() {
      const filters = this.getCookieFilters();

      if (!filters) return;

      const { basic } = filters;

      this.activeSortBy = basic.activeSortBy;
      this.activeStatus = basic.activeStatus;
    },
    watchersList() {
      const unwatchActiveTeam = this.$watch(
        'activeTeam',
        this.resetAndFetchData
      );
      const unwatchConversationInbox = this.$watch(
        'conversationInbox',
        this.resetAndFetchData
      );
      const unwatchLabel = this.$watch('label', this.resetAndFetchData);
      const unwatchProductLabel = this.$watch(
        'productLabel',
        this.resetAndFetchData
      );
      const unwatchTopicLabel = this.$watch(
        'topicLabel',
        this.resetAndFetchData
      );
      const unwatchConcurrentLabel = this.$watch(
        'concurrentLabel',
        this.resetAndFetchData
      );
      const unwatchConversationType = this.$watch(
        'conversationType',
        this.resetAndFetchData
      );
      const unwatchActiveFolder = this.$watch(
        'activeFolder',
        this.resetAndFetchData
      );
      const unwatchChatLists = this.$watch('chatLists', () => {
        this.chatsOnView = this.conversationList;
      });

      this.watchers = [
        unwatchActiveTeam,
        unwatchConversationInbox,
        unwatchLabel,
        unwatchProductLabel,
        unwatchTopicLabel,
        unwatchConcurrentLabel,
        unwatchConversationType,
        unwatchActiveFolder,
        unwatchChatLists,
      ];
    },
    navigateToPriorityContacts() {
      const accountId = this.$route.params.accountId;
      this.$router.push(`/app/accounts/${accountId}/priority-contacts`);
    },
    restartGlowEffect() {
      this.showGlowBorder = false;
      this.$nextTick(() => {
        setTimeout(() => {
          this.showGlowBorder = true;
        }, 300);
      });
    },
  },
};
</script>

<style scoped lang="scss">
@import '~dashboard/assets/scss/woot';

.alert-filter {
  padding: var(--space-small);
  background: var(--r-50);
  border-radius: var(--border-radius-small);

  @media screen and (min-height: 1040px) {
    display: none;
  }

  div:first-of-type {
    margin-top: auto;
    margin-bottom: auto;

    svg {
      color: var(--r-800);
    }
  }

  div:last-child {
    h3 {
      font-size: var(--font-size-default);
      font-weight: var(--font-weight-black);
    }

    p {
      font-size: var(--font-size-mini);
      line-height: 1.5;
      margin-bottom: 0;
    }
  }
}

.spinner {
  margin-top: var(--space-normal);
  margin-bottom: var(--space-normal);
}

.hide-on-mobile {
  @media screen and (max-width: 768px) {
    display: none !important;
  }
}

.conversations-list {
  // Prevent the list from scrolling if the submenu is opened
  &.is-context-menu-open {
    overflow: hidden !important;
  }
}

.conversations-list-wrap {
  flex-shrink: 0;
  flex-basis: clamp(32rem, 4vw + 34rem, 44rem);
  overflow: hidden;

  &.hide {
    display: none;
  }

  &.list--full-width {
    flex-basis: 100%;
  }

  .page-sub-title {
    font-size: var(--font-size-two);
  }
}

.filter--actions {
  align-self: flex-start;
  display: flex;
  align-items: center;
  gap: var(--space-smaller);
  margin-top: 4px;
}

.layout-switch {
  display: flex;
  align-items: center;
  margin-right: 6px;
}

.chat-list__top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.chat-list__header-left {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.filter-left {
  display: flex;
  align-items: center;
  gap: 6px;
}

.filter__applied {
  padding-bottom: var(--space-slab) !important;
  border-bottom: 1px solid var(--color-border);
}

.tab--chat-type {
  padding: 0 var(--space-normal);

  ::v-deep {
    .tabs {
      padding: 0;
    }
  }
}

.conversation--status-pill {
  background: var(--color-background);
  border-radius: var(--border-radius-small);
  color: var(--color-medium-gray);
  font-size: var(--font-size-micro);
  font-weight: var(--font-weight-medium);
  margin: var(--space-micro) var(--space-small) 0;
  padding: var(--space-smaller);
  text-transform: capitalize;
}

.chat-list__title {
  max-width: 85%;
}

.priority-tab {
  margin-left: var(--space-smaller);

  &:disabled {
    opacity: 1 !important;
  }
}

@keyframes shake {
  0% {
    transform: translateX(0);
  }
  20% {
    transform: translateX(-2px);
  }
  40% {
    transform: translateX(2px);
  }
  60% {
    transform: translateX(-2px);
  }
  80% {
    transform: translateX(2px);
  }
  100% {
    transform: translateX(0);
  }
}

@keyframes subtle-border-glow {
  0% {
    box-shadow: 0 0 1px rgba(255, 0, 0, 0.3);
  }
  30% {
    box-shadow: 0 0 3px rgba(255, 0, 0, 0.5);
  }
  60% {
    box-shadow: 0 0 6px rgba(255, 0, 0, 0.7);
  }
  100% {
    box-shadow: 0 0 1px rgba(255, 0, 0, 0.3);
  }
}

.shake {
  animation: shake 0.3s ease-in-out;
}

.glow-border {
  border: 1px solid rgba(255, 0, 0, 0.7);
  border-radius: 6px;
  animation: subtle-border-glow 2.5s infinite ease-in-out;
}

.status-filter-wrapper {
  margin-top: 10px;
  display: flex;
  align-items: center;
}

.status-buttons {
  display: flex;
  align-items: center;
  gap: 10px;
}

.status-button {
  background: var(--color-background);
  color: var(--color-body);
  border: 1px solid var(--color-border-light);
  padding: 4px 14px;
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  text-transform: lowercase;
  min-height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;

  &.status-button--active {
    background-color: var(--c-500) !important;
    color: var(--white) !important;
    border-color: var(--c-500) !important;
  }

  &:hover:not(.status-button--active) {
    background-color: var(--color-background-light);
  }
}

.filter-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}
</style>
